import { usePostMagicLinkMutation } from "@/queries/auth/mutations";
import { TestType } from "@/queries/types";
import { getTestType } from "@/utils/test-type";
import styled from "@emotion/styled";
import { Trans } from "@lingui/react/macro";
import { CompanyLogo } from "@togglhire/candidate-components";
import Image from "next/image";
import { useRouter } from "next/router";
import { useFlashMessages } from "../flash-messages";
import { Header } from "../header/header";
import illustration from "./assets/illustration.svg";
import { EmailForm } from "./email-form";

interface CodeWarningPageProps {
  jobOpeningName: string;
  testType: TestType;
  companyName: string;
  companyLogoUrl?: string;
  dismiss: () => void;
}

export function CodeWarningPage(props: CodeWarningPageProps) {
  const { query } = useRouter();
  const jobOpeningSlug = query.slug as string;
  const testSlug = query.hashedTestId as string;
  const magicLinkMutation = usePostMagicLinkMutation();
  const flash = useFlashMessages();

  return (
    <Page>
      <HeaderWrapper>
        <Header
          title={props.jobOpeningName}
          subtitle={getTestType(props.testType)}
          logo={
            <CompanyLogo
              logoUrl={props.companyLogoUrl}
              alt={props.companyName}
            />
          }
        />
      </HeaderWrapper>

      <Illustration>
        <Image src={illustration} alt="image" />
      </Illustration>

      <Content>
        <section>
          <Title>
            <Trans>Bigger screen remommended!</Trans>
          </Title>
          <Description>
            <Trans>
              You can take this test <strong>only once</strong> and it contains
              code input questions. We recommend using a bigger screen for a
              better experience.
            </Trans>
          </Description>
        </section>

        <Separator />

        <EmailForm
          sendEmail={(email) => {
            return magicLinkMutation.mutateAsync(
              {
                data: {
                  email,
                  job_opening_slug: jobOpeningSlug,
                  test_slug: testSlug,
                },
              },
              {
                onSuccess: () => {
                  flash.addMessage({ type: "email_sent" });
                },
              },
            );
          }}
          dismiss={props.dismiss}
        />
      </Content>
    </Page>
  );
}

const Page = styled.div`
  display: grid;
  grid-template-rows: max-content max-content auto;
  min-height: 100svh;
  background-color: ${(props) => props.theme.colors.white};
  position: relative;
`;

const HeaderWrapper = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Illustration = styled.div`
  height: 136px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 16px 16px 16px;

  @media (min-width: 768px) {
    padding: 32px 36px 24px 36px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => props.theme.typography.colorPrimary};
`;

const Description = styled.p`
  margin: 8px 0 0 0;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.typography.colorPrimary};
`;

const Separator = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.purple[10]};
  height: 1px;
  margin: 0;
`;
